import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Activity, Battery, AlertTriangle, Zap, CheckCircle2, RefreshCw, RadioTower, LineChart, Gauge, Clock3, Database, X, Search, ChevronDown, Filter, Clock, ArrowUpDown, CloudDownload, WifiOff, Siren, ChevronRight, Wrench } from 'lucide-react';
import LoadingSpinner from '../../src/components/LoadingSpinner';
import { api } from '../../src/services/api';
import { supabase } from '../../src/lib/supabase';
import { UnidadeGeradora, LeituraTelemetria, GrowattMetric, AlarmeGeradora, SeveridadeAlarme, EventoAlarme, FonteAlarme } from '../../types';
import { agruparAlarmes, duracaoEvento, tituloEvento } from '../../src/lib/alarmesGeradoras';
import CurvaPotencia, { PontoSerie, Granularidade } from './CurvaPotencia';
import {
    getVendor, mapStatus, isEstadoOperando, PlantState,
    formatPower, formatKwh, formatDateTime, getRelativeTime, getDurationText, getFreshness,
    getFatorCapacidade, getDisplayUsina, capacidadeMwpToW,
    potenciaEfetivaW, deltaOr, tempoSemReportar, ESTADO_LABEL,
} from './geradorasShared';


// Painel operacional das usinas (aba "Painel" de /geradoras): lista, status,
// alarmes e KPIs do agora. A leitura histórica — curva horária e comparação de
// períodos — vive na aba "Análise" (Analise/AnaliseView.tsx), que agrega no
// Postgres via RPC em vez de baixar leitura crua.


const DB_POLL_MS = 60 * 1000;
// Não redispara as APIs dos fabricantes (Growatt ~54s) mais de 1×/min mesmo com
// cliques repetidos no botão — o cron já mantém o cache quente a cada 10 min.
const FABRICANTES_MIN_INTERVAL_MS = 60 * 1000;

type FonteExterna = 'growatt' | 'canadian' | 'chint';
const FONTES_EXTERNAS: { key: FonteExterna; label: string }[] = [
    { key: 'growatt', label: 'Growatt' },
    { key: 'canadian', label: 'Canadian' },
    { key: 'chint', label: 'Chint' },
];

// ---------------- UI: badge de status ----------------

const STATE_STYLE: Record<PlantState, { badge: string; label: string; icon: React.ReactNode }> = {
    operando: { badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800', label: ESTADO_LABEL.operando, icon: <CheckCircle2 size={12} className="stroke-[2.5]" /> },
    parcial: { badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800 animate-pulse', label: ESTADO_LABEL.parcial, icon: <AlertTriangle size={12} className="stroke-[2.5]" /> },
    offline: { badge: 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600', label: ESTADO_LABEL.offline, icon: <WifiOff size={12} className="stroke-[2.5]" /> },
    falha: { badge: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 border-rose-200 dark:border-rose-800', label: ESTADO_LABEL.falha, icon: <AlertTriangle size={12} className="stroke-[2.5]" /> },
    manutencao: { badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800', label: ESTADO_LABEL.manutencao, icon: <Activity size={12} className="stroke-[2.5]" /> },
    desconhecido: { badge: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700', label: ESTADO_LABEL.desconhecido, icon: <RadioTower size={12} className="stroke-[2.5]" /> },
};

const StatusBadge = ({ state, statusCode, lastUpdateTime }: { state: PlantState; statusCode: number | null; lastUpdateTime?: string | null }) => {
    const style = STATE_STYLE[state];
    const label = state === 'desconhecido' && statusCode !== null ? `Status ${statusCode}` : style.label;
    const duration = (state !== 'operando' && state !== 'desconhecido') ? getDurationText(lastUpdateTime) : null;
    return (
        <div className="flex flex-col gap-1 items-start">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${style.badge} shadow-sm uppercase tracking-wider`}>
                {style.icon}
                {label}
            </span>
            {duration && (
                <span className="text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-tight pl-1">{duration}</span>
            )}
        </div>
    );
};

// ---------------- UI: alarmes ----------------

const SEVERIDADE_STYLE: Record<SeveridadeAlarme, { chip: string; label: string; dot: string }> = {
    critica: { chip: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800', label: 'Crítico', dot: 'bg-rose-500' },
    atencao: { chip: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800', label: 'Atenção', dot: 'bg-amber-500' },
    informativa: { chip: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600', label: 'Informativo', dot: 'bg-gray-400' },
};

const FONTE_ALARME_LABEL: Record<FonteAlarme, string> = {
    chint: 'Chint', canadian: 'Canadian', growatt: 'Growatt', saj: 'SAJ',
};

// ---------------- UI: períodos do painel executivo ----------------
// A granularidade acompanha a janela: hora até 7 dias, dia até 12 meses, mês no
// "tudo". Sem isso, 6 meses em granularidade horária dariam ~4.300 pontos num
// gráfico de 800px — mais pontos do que pixels.
type ChavePeriodo = '24h' | '7d' | '30d' | '12m' | 'tudo';
const PERIODOS: Array<{ chave: ChavePeriodo; label: string; dias: number | null; gran: Granularidade }> = [
    { chave: '24h', label: '24 h', dias: 1, gran: 'hora' },
    { chave: '7d', label: '7 dias', dias: 7, gran: 'hora' },
    { chave: '30d', label: '30 dias', dias: 30, gran: 'dia' },
    { chave: '12m', label: '12 meses', dias: 365, gran: 'mes' },
    { chave: 'tudo', label: 'Tudo', dias: null, gran: 'mes' },
];

/** Plural sem gambiarra de template no meio do JSX. */
const plural = (n: number, um: string, muitos: string) => `${n} ${n === 1 ? um : muitos}`;

/**
 * Corpo do alarme aberto: o que é, efeito, o que fazer, inversores e os campos
 * crus. Vive separado porque aparece em DOIS lugares — no histórico geral e no
 * painel executivo da usina — e o texto do catálogo não pode divergir entre eles.
 */
const DetalheAlarme = ({ evento }: { evento: EventoAlarme }) => {
    const a = evento.representante;
    const cat = a.catalogo || null;
    return (
        <div className="space-y-3 text-sm">
            {cat ? (
                <>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">O que é</div>
                        <p className="text-gray-700 dark:text-gray-200 mt-1">{cat.o_que_e}</p>
                    </div>
                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">Efeito na geração</div>
                        <p className="text-gray-700 dark:text-gray-200 mt-1">{cat.impacto}</p>
                    </div>
                    <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 p-3">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                            <Wrench size={12} /> O que fazer
                        </div>
                        <p className="text-emerald-900 dark:text-emerald-100 mt-1">{cat.acao}</p>
                    </div>
                    {cat.fonte_texto === 'nao_confirmado' ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            O manual do inversor não cobre este código. O texto acima diz o que se sabe e o que não
                            se sabe — confirme com o suporte do fabricante antes de deslocar equipe.
                        </p>
                    ) : null}
                </>
            ) : (
                <p className="text-gray-600 dark:text-gray-300">
                    Código ainda sem tradução no catálogo. O fabricante descreve como
                    <span className="font-semibold"> {a.error_code_info || '—'}</span>. Vale confirmar
                    com o suporte antes de agir.
                </p>
            )}

            <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500">
                    Inversores ({evento.inversores.length})
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {evento.inversores.map(sn => (
                        <span key={sn} className="px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700 text-[11px] font-mono text-gray-600 dark:text-gray-300">
                            {sn}
                        </span>
                    ))}
                </div>
            </div>

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500 font-mono pt-1">
                <span>codigo: {a.error_code}</span>
                {a.error_code_info ? <span>variante: {a.error_code_info}</span> : null}
                <span>fonte: {a.fonte}</span>
                <span>inicio: {formatDateTime(evento.inicio)}</span>
                {evento.fim ? <span>fim: {formatDateTime(evento.fim)}</span> : null}
                {a.severidade_fabricante ? <span>fabricante: {a.severidade_fabricante}</span> : null}
            </div>
        </div>
    );
};

/**
 * Um evento de alarme: a mesma falha, na mesma usina, atingindo N inversores.
 *
 * Fica no módulo, e não dentro do PainelUsinas, para não ser recriado a cada
 * render do painel (que repolla o banco de minuto em minuto).
 */
const CartaoEvento = ({ evento, nomeUsina, aberto, onToggle }: {
    evento: EventoAlarme;
    nomeUsina: string;
    aberto: boolean;
    onToggle: () => void;
}) => {
    const a = evento.representante;
    const cat = a.catalogo || null;
    const style = SEVERIDADE_STYLE[cat?.severidade || 'atencao'];
    const duracao = duracaoEvento(evento);
    const titulo = tituloEvento(evento);

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60 overflow-hidden">
            <button
                onClick={onToggle}
                className="w-full text-left px-4 py-3.5 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
            >
                <span className={`mt-1 w-2.5 h-2.5 rounded-full flex-shrink-0 ${evento.encerrado ? 'bg-gray-300 dark:bg-gray-600' : style.dot}`} />
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="font-poppins font-bold text-gray-900 dark:text-white">{titulo}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${style.chip}`}>
                            {style.label}
                        </span>
                        {evento.encerrado ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800">
                                Normalizado{duracao ? ` · ${duracao}` : ''}
                            </span>
                        ) : (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-rose-50 dark:bg-rose-900/25 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800">
                                Sem fim registrado
                            </span>
                        )}
                        {cat?.fonte_texto === 'nao_confirmado' ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600">
                                Não confirmado
                            </span>
                        ) : null}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {nomeUsina}
                        {' · '}{FONTE_ALARME_LABEL[a.fonte]}
                        {' · '}{formatDateTime(evento.inicio)}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {plural(evento.inversores.length, 'inversor atingido', 'inversores atingidos')}
                        {evento.disparos > evento.inversores.length
                            ? ` · ${plural(evento.disparos, 'disparo', 'disparos')}`
                            : ''}
                    </div>
                </div>
                <ChevronRight size={18} className={`flex-shrink-0 mt-1 text-gray-400 transition-transform ${aberto ? 'rotate-90' : ''}`} />
            </button>

            {aberto ? (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700">
                    <DetalheAlarme evento={evento} />
                </div>
            ) : null}
        </div>
    );
};

// ---------------- UI: chip de fonte de dados ----------------

type ChipStatus = 'idle' | 'loading' | 'ok' | 'error';
const FonteChip = ({ label, status, at }: { label: string; status: ChipStatus; at: string | null }) => {
    const dot = status === 'loading'
        ? 'bg-blue-500 animate-pulse'
        : status === 'error'
            ? 'bg-rose-500'
            : status === 'ok'
                ? ({ fresh: 'bg-emerald-500', stale: 'bg-amber-500', old: 'bg-rose-400', none: 'bg-gray-400' })[getFreshness(at)]
                : 'bg-gray-300 dark:bg-gray-600';
    const detail = status === 'loading' ? 'atualizando…' : status === 'error' ? 'sem dados' : at ? getRelativeTime(at) : '—';
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-[11px] font-semibold text-gray-600 dark:text-gray-300 shadow-sm"
            title={at ? `${label}: ${formatDateTime(at)}` : label}
        >
            <span className={`w-2 h-2 rounded-full ${dot}`} />
            {label}
            <span className="font-normal text-gray-400 dark:text-gray-500">{detail}</span>
        </span>
    );
};

// ---------------- UI: dropdown de filtro genérico ----------------

interface DropdownOption<T extends string | number> { value: T; label: string; icon?: React.ReactNode }

function FilterDropdown<T extends string | number>({ tag, valueLabel, options, value, onChange, icon, highlight }: {
    tag: string;
    valueLabel: string;
    options: DropdownOption<T>[];
    value: T;
    onChange: (v: T) => void;
    icon: React.ReactNode;
    highlight: boolean;
}) {
    const [open, setOpen] = useState(false);
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className={`flex items-center gap-2.5 pl-3.5 pr-3 py-2 bg-white dark:bg-gray-800 border rounded-full shadow-sm hover:shadow-md transition-all duration-200 group w-full sm:w-auto justify-between sm:justify-start ${open ? 'border-primary ring-2 ring-primary/20 dark:ring-primary/30' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${highlight ? 'bg-gradient-to-br from-primary/20 to-primary/5 text-primary' : 'bg-gray-100 dark:bg-gray-700 text-gray-400 group-hover:text-primary/70'}`}>
                        {icon}
                    </div>
                    <div className="text-left min-w-0">
                        <p className="text-[9px] text-gray-400 dark:text-gray-500 font-medium uppercase tracking-wider leading-none mb-0.5">{tag}</p>
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{valueLabel}</p>
                    </div>
                </div>
                <ChevronDown size={14} className={`text-gray-400 dark:text-gray-500 transition-transform duration-300 flex-shrink-0 ml-2 ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <>
                    <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
                    <div className="absolute top-full left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 p-1.5 z-50 animate-fade-in">
                        {options.map((opt) => (
                            <button
                                key={String(opt.value)}
                                onClick={() => { onChange(opt.value); setOpen(false); }}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all duration-150 flex items-center justify-between group/item mb-0.5 last:mb-0 ${value === opt.value ? 'bg-primary/10 text-primary font-bold' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 font-medium'}`}
                            >
                                <span className="flex items-center gap-2.5 truncate">
                                    {opt.icon && <span className={value === opt.value ? 'text-primary' : 'text-gray-300 dark:text-gray-600 group-hover/item:text-gray-400'}>{opt.icon}</span>}
                                    {opt.label}
                                </span>
                                {value === opt.value && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

// ---------------- View ----------------

/** `usinaInicial`: a Visão 3D manda para cá com o painel executivo de uma usina já aberto. */
const PainelUsinas: React.FC<{ usinaInicial?: string | null }> = ({ usinaInicial = null }) => {
    const [unidades, setUnidades] = useState<UnidadeGeradora[]>([]);
    const [leituras, setLeituras] = useState<Record<string, LeituraTelemetria>>({});
    const [leiturasHistorico, setLeiturasHistorico] = useState<Record<string, LeituraTelemetria[]>>({});
    const [baselines, setBaselines] = useState<Record<string, Record<string, number | null>>>({});
    const [metrics, setMetrics] = useState<Record<string, GrowattMetric>>({});
    const [fabricantesRefreshing, setFabricantesRefreshing] = useState(false);
    const lastFabricantesTriggerRef = useRef<number>(0);
    const [selectedSiteId, setSelectedSiteId] = useState<string | null>(usinaInicial);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(!!usinaInicial);
    // Histórico do painel executivo: buscado sob demanda, por usina. O fetch
    // global cobre só 48h de todas as usinas juntas — os Blocos têm 40 mil
    // leituras desde março, e o modal precisa alcançar isso.
    const [periodo, setPeriodo] = useState<ChavePeriodo>('7d');
    const [serie, setSerie] = useState<PontoSerie[]>([]);
    const [leiturasModal, setLeiturasModal] = useState<LeituraTelemetria[]>([]);
    const [carregandoSerie, setCarregandoSerie] = useState(false);
    // A tabela vinha com 500 linhas quase todas iguais (usina parada grava 0 W de
    // 5 em 5 min). Sem filtro é impossível achar o que interessa.
    const [filtroLeitura, setFiltroLeitura] = useState<'todas' | 'geracao' | 'zeradas'>('todas');
    const [buscaLeitura, setBuscaLeitura] = useState('');
    const [alarmes, setAlarmes] = useState<AlarmeGeradora[]>([]);
    const [isAlarmesOpen, setIsAlarmesOpen] = useState(false);
    const [alarmeExpandido, setAlarmeExpandido] = useState<number | null>(null);
    const [searchFilter, setSearchFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'operando' | 'falha'>('all');
    const [sortFilter, setSortFilter] = useState<'nome' | 'potencia' | 'prod30d'>('nome');
    const [janelaAnaliseHoras, setJanelaAnaliseHoras] = useState<6 | 12 | 24>(24);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // A notificação de alarme aponta para /geradoras?alarmes=1. Como o app usa
    // HashRouter, a query vive dentro do hash — daí a leitura manual.
    useEffect(() => {
        const query = window.location.hash.split('?')[1] || '';
        if (new URLSearchParams(query).get('alarmes') === '1') setIsAlarmesOpen(true);
    }, []);

    // -------- fetch: banco (telemetria do coletor, 48h) --------
    const fetchDb = useCallback(async () => {
        const [unis, leits, alrms] = await Promise.all([
            api.geradoras.getUnidades(),
            api.geradoras.getLeiturasRecentes(),
            api.geradoras.getAlarmes().catch(() => [] as AlarmeGeradora[]),
        ]);
        setUnidades(unis);
        setAlarmes(alrms);

        const leiturasMap: Record<string, LeituraTelemetria> = {};
        const historicoMap: Record<string, LeituraTelemetria[]> = {};
        leits.forEach(l => {
            if (!leiturasMap[l.site_id_api]) leiturasMap[l.site_id_api] = l;
            (historicoMap[l.site_id_api] = historicoMap[l.site_id_api] || []).push(l);
        });
        Object.values(historicoMap).forEach(items => {
            items.sort((a, b) => new Date(a.data_leitura).getTime() - new Date(b.data_leitura).getTime());
        });
        setLeituras(leiturasMap);
        setLeiturasHistorico(historicoMap);
        setLoading(false);
    }, []);

    // -------- fetch: cache de métricas dos fabricantes (leitura instantânea) --------
    const fetchCache = useCallback(async () => {
        const rows = await api.geradoras.getMetricasCache();
        const map: Record<string, GrowattMetric> = {};
        rows.forEach(m => { map[m.site_id_api] = m; });
        setMetrics(map);
    }, []);

    // -------- fetch: baselines de energia (RPC, ~25 linhas em 1 request) --------
    const fetchBaselines = useCallback(async () => {
        const b = await api.geradoras.getEnergiaBaselines();
        setBaselines(b);
    }, []);

    // Dispara as APIs dos fabricantes em background (Growatt ~54s) e relê o cache
    // quando terminam. Nunca bloqueia a tela. Throttle p/ não hammerar a Growatt.
    const triggerFabricantes = useCallback((force: boolean) => {
        if (!force && Date.now() - lastFabricantesTriggerRef.current < FABRICANTES_MIN_INTERVAL_MS) return;
        lastFabricantesTriggerRef.current = Date.now();
        setFabricantesRefreshing(true);
        api.geradoras.refreshMetricasFabricantes()
            .finally(() => { fetchCache().catch(() => { }).finally(() => setFabricantesRefreshing(false)); });
    }, [fetchCache]);

    // "Sincronizar agora": relê tudo do banco/cache (instantâneo) e dispara a
    // atualização dos fabricantes em background — o spinner do botão não fica
    // preso nos ~54s da Growatt; os valores já aparecem do cache.
    const handleManualSync = useCallback(async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        try {
            await Promise.allSettled([fetchDb(), fetchCache(), fetchBaselines()]);
        } finally {
            setIsRefreshing(false);
        }
        triggerFabricantes(false);
    }, [isRefreshing, fetchDb, fetchCache, fetchBaselines, triggerFabricantes]);

    // Carga inicial + polling do banco/cache a cada 60s (fallback do realtime).
    // Não dispara mais os fabricantes no mount: o cron de 10 em 10 min
    // (geradoras-cache-growatt/canadian) já mantém o cache quente, e cada tela
    // aberta disparando os ~53s do Growatt em cima do cron só piorava o rate-limit.
    // O botão "Sincronizar agora" continua disparando sob demanda.
    useEffect(() => {
        fetchDb().catch(err => { console.error('[Geradoras] fetch banco:', err); setLoading(false); });
        fetchCache().catch(err => console.error('[Geradoras] fetch cache:', err));
        fetchBaselines().catch(err => console.error('[Geradoras] baselines:', err));

        const interval = setInterval(() => {
            fetchDb().catch(err => console.error('[Geradoras] poll banco:', err));
            fetchCache().catch(err => console.error('[Geradoras] poll cache:', err));
        }, DB_POLL_MS);
        return () => clearInterval(interval);
    }, [fetchDb, fetchCache, fetchBaselines]);

    // Painel executivo: busca a série da usina aberta, na janela escolhida.
    // Só roda com o modal aberto — não faz sentido pagar isso na lista.
    useEffect(() => {
        if (!isDetailModalOpen || !selectedSiteId) return;
        const cfg = PERIODOS.find(p => p.chave === periodo)!;
        let cancelado = false;
        setCarregandoSerie(true);
        const fim = new Date();
        // 'tudo' = desde antes do primeiro dado do projeto; a RPC recorta sozinha.
        const inicio = cfg.dias == null
            ? new Date('2020-01-01T00:00:00Z')
            : new Date(Date.now() - cfg.dias * 864e5);
        Promise.all([
            api.geradoras.getSerieUsina(selectedSiteId, inicio, fim, cfg.gran),
            api.geradoras.getLeiturasUsina(selectedSiteId, inicio, fim, 500),
        ])
            .then(([s, l]) => {
                // Descarta resposta de um período que o usuário já trocou.
                if (cancelado) return;
                setSerie(s);
                setLeiturasModal(l);
            })
            .catch(() => { if (!cancelado) { setSerie([]); setLeiturasModal([]); } })
            .finally(() => { if (!cancelado) setCarregandoSerie(false); });
        return () => { cancelado = true; };
    }, [isDetailModalOpen, selectedSiteId, periodo]);

    // Realtime: novas leituras do coletor entram na tela sem precisar de botão
    useEffect(() => {
        const channel = supabase
            .channel(`geradoras-telemetria-${Math.random().toString(36).slice(2, 8)}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'leituras_telemetria' }, (payload) => {
                const nova = payload.new as LeituraTelemetria;
                if (!nova?.site_id_api) return;
                setLeituras(prev => {
                    const atual = prev[nova.site_id_api];
                    if (atual && new Date(atual.data_leitura) >= new Date(nova.data_leitura)) return prev;
                    return { ...prev, [nova.site_id_api]: nova };
                });
                setLeiturasHistorico(prev => ({
                    ...prev,
                    [nova.site_id_api]: [...(prev[nova.site_id_api] || []), nova],
                }));
            })
            .subscribe(status => {
                if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
                    console.warn('[Geradoras] realtime indisponível (segue no polling de 60s):', status);
                }
            });
        return () => { supabase.removeChannel(channel); };
    }, []);

    // -------- derivações --------
    // Todas as 14 usinas (7 Chint + 4 Growatt + 3 Canadian) passaram a ter linha
    // em unidades_geradoras em 24/08/2026. Antes existia aqui uma lista hardcoded
    // para as que não tinham cadastro — virou fonte de divergência de nome e de
    // capacidade (as Growatt apareciam sem MWp, logo sem fator de capacidade).
    const unidadesExibidas = useMemo(() => {
        const map = new Map<string, UnidadeGeradora>();
        unidades.forEach(u => map.set(u.site_id_api, u));
        return Array.from(map.values()).sort((a, b) => a.nome_usina.localeCompare(b.nome_usina, 'pt-BR'));
    }, [unidades]);

    // Freshness por fonte a partir do cache (max atualizado_em por fonte)
    const fontesSync = useMemo(() => {
        const acc: Record<FonteExterna, string | null> = { growatt: null, canadian: null, chint: null };
        Object.values(metrics).forEach(m => {
            const f = (m.fonte as FonteExterna) || undefined;
            if (!f || !(f in acc)) return;
            if (!acc[f] || (m.atualizado_em && new Date(m.atualizado_em) > new Date(acc[f]!))) {
                acc[f] = m.atualizado_em || acc[f];
            }
        });
        return acc;
    }, [metrics]);

    const statusReal = useMemo(() => {
        const map: Record<string, { code: number | null; state: PlantState }> = {};
        unidadesExibidas.forEach(u => {
            const code = metrics[u.site_id_api]?.status_code ?? u.status_atual;
            map[u.site_id_api] = { code, state: mapStatus(getVendor(u.site_id_api, u.fabricante), code) };
        });
        return map;
    }, [unidadesExibidas, metrics]);

    /** Potência efetiva: telemetria fresca (<30min) > plataforma do fabricante > telemetria velha. */
    const getPotenciaAtualW = useCallback(
        (siteId: string): number | null => potenciaEfetivaW(leituras[siteId], metrics[siteId]?.power_w),
        [leituras, metrics]);

    const metricasPorUsina = useMemo(() => {
        const map: Record<string, { prod1d: number | null; prod15d: number | null; prod30d: number | null; prod12m: number | null; prodTotal: number | null }> = {};
        unidadesExibidas.forEach(usina => {
            const site = usina.site_id_api;
            const gm = metrics[site];
            const b = baselines[site] || {};
            const latest = b.latest ?? null;
            map[site] = {
                prod1d: deltaOr(latest, b.base_1d, gm?.prod_1d_kwh),
                prod15d: deltaOr(latest, b.base_15d, gm?.prod_15d_kwh),
                prod30d: deltaOr(latest, b.base_30d, gm?.prod_30d_kwh),
                prod12m: deltaOr(latest, b.base_365d, gm?.prod_12m_kwh),
                prodTotal: latest ?? gm?.prod_total_kwh ?? null,
            };
        });
        return map;
    }, [unidadesExibidas, baselines, metrics]);

    const resumoEnergia = useMemo(() => {
        const producao: Record<'1d' | '15d' | '30d' | '12m' | 'total', number> = { '1d': 0, '15d': 0, '30d': 0, '12m': 0, total: 0 };
        unidadesExibidas.forEach(usina => {
            const m = metricasPorUsina[usina.site_id_api];
            if (!m) return;
            producao['1d'] += m.prod1d || 0;
            producao['15d'] += m.prod15d || 0;
            producao['30d'] += m.prod30d || 0;
            producao['12m'] += m.prod12m || 0;
            producao.total += m.prodTotal || 0;
        });
        return producao;
    }, [unidadesExibidas, metricasPorUsina]);

    const potenciaTotal = useMemo(() => {
        let soma = 0;
        let reportando = 0;
        unidadesExibidas.forEach(u => {
            const p = getPotenciaAtualW(u.site_id_api);
            if (p != null) { soma += p; reportando++; }
        });
        return { somaW: soma, reportando };
    }, [unidadesExibidas, getPotenciaAtualW]);

    const capacidadeTotalMwp = unidadesExibidas.reduce((acc, u) => acc + (u.capacidade_instalada || 0), 0);
    const usinasOperando = unidadesExibidas.filter(u => isEstadoOperando(statusReal[u.site_id_api]?.state)).length;
    const usinasParciais = unidadesExibidas.filter(u => statusReal[u.site_id_api]?.state === 'parcial').length;

    const ultimaTelemetria = useMemo(() => {
        let max: string | null = null;
        Object.values(leituras).forEach(l => {
            if (!max || new Date(l.data_leitura) > new Date(max)) max = l.data_leitura;
        });
        return max;
    }, [leituras]);

    const unidadesFiltradas = useMemo(() => {
        const termo = searchFilter.trim().toLowerCase();
        const base = unidadesExibidas.filter(u => {
            const operando = isEstadoOperando(statusReal[u.site_id_api]?.state);
            if (statusFilter === 'operando' && !operando) return false;
            if (statusFilter === 'falha' && operando) return false;
            if (!termo) return true;
            return u.nome_usina.toLowerCase().includes(termo) || u.site_id_api.toLowerCase().includes(termo);
        });
        if (sortFilter === 'potencia') {
            return [...base].sort((a, b) => (getPotenciaAtualW(b.site_id_api) || 0) - (getPotenciaAtualW(a.site_id_api) || 0));
        }
        if (sortFilter === 'prod30d') {
            return [...base].sort((a, b) => (metricasPorUsina[b.site_id_api]?.prod30d || 0) - (metricasPorUsina[a.site_id_api]?.prod30d || 0));
        }
        return [...base].sort((a, b) => a.nome_usina.localeCompare(b.nome_usina, 'pt-BR'));
    }, [unidadesExibidas, searchFilter, statusFilter, sortFilter, statusReal, getPotenciaAtualW, metricasPorUsina]);

    // Rótulo por fabricante: fonte única em geradorasShared, compartilhada com
    // a aba Análise para as duas não divergirem.
    const getDisplay = getDisplayUsina;

    /** Timestamp do dado mais recente daquela usina (telemetria ou plataforma). */
    /**
     * Quando a USINA reportou dado pela última vez.
     *
     * 🔴 Isto NÃO é `leituras_telemetria.data_leitura`. Aquele campo é a hora em
     * que o nosso coletor gravou a linha, e ele grava de 5 em 5 minutos mesmo
     * quando o inversor não mandou nada novo — com potência 0 e energia_total
     * congelada. Ou seja: está sempre "agora", para toda usina, viva ou morta.
     *
     * Até 31/08/2026 esta função devolvia o MAIOR entre os dois carimbos, então
     * a hora do coletor sempre vencia e o painel dizia "há 7 min" para uma usina
     * que não reporta desde 30/01/2026 (Vinicius Cândido, 213 dias parada) e
     * "há menos de 1h" para a Qgbt1, parada havia 4 dias.
     *
     * `last_update_time` vem da plataforma do fabricante e é o único carimbo que
     * fala da usina. A hora do coletor responde outra pergunta — "o nosso robô
     * rodou?" — e tem lugar próprio no painel executivo.
     */
    const getDadoUsinaAt = (siteId: string): string | null =>
        metrics[siteId]?.last_update_time || leituras[siteId]?.data_leitura || null;

    /** Hora em que o coletor gravou por último — saúde do robô, não da usina. */
    const getColetorAt = (siteId: string): string | null =>
        leituras[siteId]?.data_leitura || null;

    // -------- modal --------
    // Uma falha de rede derruba os N inversores da usina no mesmo segundo e vem
    // uma linha por inversor. A tela mostra EVENTO, não linha — senão vira uma
    // parede de cards idênticos. Ver src/lib/alarmesGeradoras.ts.
    const eventosAlarme = useMemo(() => agruparAlarmes(alarmes), [alarmes]);

    // Badge do botão: só o que é recente o bastante para exigir ação, e contado
    // em evento. Antes contava linha, então um evento em 4 inversores marcava 4.
    const eventosRecentes = useMemo(() => {
        const corte = Date.now() - 72 * 3600000;
        return eventosAlarme.filter(e => Date.parse(e.inicio) >= corte);
    }, [eventosAlarme]);
    // Evento sem fim registrado é o que pode estar acontecendo agora.
    const eventosAbertos = useMemo(
        () => eventosRecentes.filter(e => !e.encerrado).length, [eventosRecentes]);
    const nomePorSite = useMemo(() => {
        const m: Record<string, string> = {};
        unidadesExibidas.forEach(u => { m[u.site_id_api] = u.nome_usina; });
        return m;
    }, [unidadesExibidas]);

    const usinaSelecionada = unidadesExibidas.find(u => u.site_id_api === selectedSiteId) || null;
    const leituraSelecionada = usinaSelecionada ? leituras[usinaSelecionada.site_id_api] : undefined;
    const historicoSelecionado = usinaSelecionada ? (leiturasHistorico[usinaSelecionada.site_id_api] || []) : [];

    const agora = Date.now();
    const historicoJanela = historicoSelecionado.filter(item => {
        const t = new Date(item.data_leitura).getTime();
        return Number.isFinite(t) && (agora - t <= janelaAnaliseHoras * 3_600_000);
    });
    const pontosCalculo = historicoJanela.length > 0 ? historicoJanela : historicoSelecionado;
    const potenciaMediaW = pontosCalculo.length > 0 ? pontosCalculo.reduce((acc, item) => acc + (item.potencia_atual_w || 0), 0) / pontosCalculo.length : 0;
    const potenciaPicoW = pontosCalculo.length > 0 ? Math.max(...pontosCalculo.map(item => item.potencia_atual_w || 0)) : 0;

    const ultimoPonto = pontosCalculo[pontosCalculo.length - 1];
    const primeiroPonto = pontosCalculo[0];
    const energiaJanelaKwh = ultimoPonto && primeiroPonto
        ? Math.max((ultimoPonto.energia_total_kwh || 0) - (primeiroPonto.energia_total_kwh || 0), 0)
        : 0;

    const potenciaAtualSelecionada = usinaSelecionada ? getPotenciaAtualW(usinaSelecionada.site_id_api) : null;
    const fatorCapacidadeAtual = usinaSelecionada ? getFatorCapacidade(potenciaAtualSelecionada, usinaSelecionada.capacidade_instalada) : null;
    const fatorCapacidadeMedio = usinaSelecionada ? getFatorCapacidade(Math.round(potenciaMediaW), usinaSelecionada.capacidade_instalada) : null;

    /**
     * Estatísticas da JANELA ESCOLHIDA no painel executivo.
     *
     * Vêm da série agregada no Postgres, não das 48h que a lista carrega — é o
     * que permite "30 dias" e "12 meses" sem baixar 40 mil leituras.
     */
    const statsPeriodo = useMemo(() => {
        if (serie.length === 0) return null;
        const energia = serie.reduce((s, p) => s + p.geracao_kwh, 0);
        const amostras = serie.reduce((s, p) => s + p.amostras, 0);
        const pico = serie.reduce<PontoSerie | null>(
            (melhor, p) => (!melhor || p.potencia_pico_w > melhor.potencia_pico_w ? p : melhor), null);
        const melhorBalde = serie.reduce<PontoSerie | null>(
            (melhor, p) => (!melhor || p.geracao_kwh > melhor.geracao_kwh ? p : melhor), null);
        const comGeracao = serie.filter(p => p.potencia_pico_w > 0).length;
        // Média ponderada pelo nº de amostras: baldes com menos leituras não
        // podem pesar igual aos cheios.
        const somaPond = serie.reduce((s, p) => s + p.potencia_media_w * p.amostras, 0);
        const mediaW = amostras > 0 ? somaPond / amostras : 0;

        const capW = capacidadeMwpToW(usinaSelecionada?.capacidade_instalada);
        const gran = PERIODOS.find(p => p.chave === periodo)!.gran;
        const horasPorBalde = gran === 'hora' ? 1 : gran === 'dia' ? 24 : 24 * 30;
        // Fator de capacidade do período = energia gerada / energia teórica se a
        // usina tivesse rodado no nominal o tempo todo.
        const teorico = capW ? (capW / 1000) * serie.length * horasPorBalde : 0;
        const fatorPeriodo = teorico > 0 ? Math.min((energia / teorico) * 100, 100) : null;

        return {
            energia, amostras, mediaW, comGeracao,
            picoW: pico?.potencia_pico_w ?? 0,
            picoEm: pico?.bucket ?? null,
            melhorBalde,
            fatorPeriodo,
            inicio: serie[0].bucket,
            fim: serie[serie.length - 1].bucket,
        };
    }, [serie, periodo, usinaSelecionada]);

    /**
     * Diagnóstico da divergência entre "o coletor rodou" e "a usina reportou".
     *
     * Sem isto o operador vê o carimbo do coletor e conclui que está tudo em dia.
     * Foi o que aconteceu com a Vinicius Cândido: parada desde 30/01/2026 e a
     * tela dizia "há 7 min".
     */
    const diagnosticoUsina = useMemo(() => {
        if (!usinaSelecionada) return null;
        const dadoAt = metrics[usinaSelecionada.site_id_api]?.last_update_time || null;
        const coletorAt = leituras[usinaSelecionada.site_id_api]?.data_leitura || null;
        const parada = tempoSemReportar(dadoAt);
        if (!dadoAt || !parada) return null;
        const coletorVivo = coletorAt != null && (Date.now() - Date.parse(coletorAt)) / 3600000 < 1;
        return {
            grave: parada.grave,
            titulo: `A usina não reporta há ${parada.texto}.`,
            detalhe: coletorVivo
                ? `Último dado da plataforma do fabricante: ${formatDateTime(dadoAt)}. Nosso coletor continua rodando normalmente (${formatDateTime(coletorAt!)}) e gravando leituras zeradas — por isso a lista parece atualizada. O problema está entre a usina e o fabricante, não na coleta.`
                : `Último dado da plataforma do fabricante: ${formatDateTime(dadoAt)}. O coletor também está parado, então pode ser falha da nossa integração.`,
        };
    }, [usinaSelecionada, metrics, leituras]);

    /** Leituras da tabela do painel executivo, já filtradas. */
    const leiturasFiltradas = useMemo(() => {
        const termo = buscaLeitura.trim().toLowerCase();
        return leiturasModal.filter(l => {
            const w = l.potencia_atual_w || 0;
            if (filtroLeitura === 'geracao' && w <= 0) return false;
            if (filtroLeitura === 'zeradas' && w > 0) return false;
            // Busca no timestamp já formatado: é assim que ele aparece na tela,
            // então "31/08" e "14:2" funcionam como o operador espera.
            return !termo || formatDateTime(l.data_leitura).toLowerCase().includes(termo);
        });
    }, [leiturasModal, filtroLeitura, buscaLeitura]);

    /** Baixa o que está na tela — respeita o filtro, não a carga inteira. */
    const exportarLeiturasCsv = useCallback(() => {
        if (!usinaSelecionada || leiturasFiltradas.length === 0) return;
        const cab = ['timestamp', 'potencia_w', 'energia_hoje_kwh', 'energia_mes_kwh', 'energia_total_kwh'];
        const linhas = leiturasFiltradas.map(l => [
            l.data_leitura, l.potencia_atual_w ?? '', l.energia_hoje_kwh ?? '',
            l.energia_mes_kwh ?? '', l.energia_total_kwh ?? '',
        ].join(';'));
        // ; e BOM porque o destino é Excel em pt-BR, que senão joga tudo numa coluna
        // e come os acentos.
        const blob = new Blob(['﻿' + [cab.join(';'), ...linhas].join('\r\n')],
            { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `leituras_${usinaSelecionada.site_id_api}_${periodo}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }, [leiturasFiltradas, usinaSelecionada, periodo]);

    /** Alarmes desta usina, já agrupados por evento. */
    const eventosDaUsina = useMemo(
        () => (selectedSiteId ? eventosAlarme.filter(e => e.representante.site_id_api === selectedSiteId) : []),
        [eventosAlarme, selectedSiteId],
    );

    const pontosGrafico = pontosCalculo.slice(-24);
    const maxGraficoW = Math.max(...pontosGrafico.map(p => p.potencia_atual_w || 0), 1);
    // Usina parada devolve 24 leituras zeradas. Sem isto o gráfico desenhava 24
    // tocos iguais (a altura tinha piso de 6%), o que parece gráfico quebrado em
    // vez de "não gerou nada" — e é o estado normal de madrugada.
    const graficoZerado = pontosGrafico.length > 0
        && pontosGrafico.every(p => !p.potencia_atual_w);

    if (loading && unidades.length === 0) {
        return <LoadingSpinner label="Carregando telemetria em tempo real..." size="lg" />;
    }

    return (
        <>
            <div className="space-y-8 animate-fade-in pb-12 w-full max-w-[95vw] mx-auto">
                {/* Header + painel de sincronização */}
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-3xl font-poppins font-bold text-gray-900 dark:text-white">Op. Geradoras</h1>
                            {(isRefreshing || fabricantesRefreshing) && <RefreshCw size={18} className="animate-spin text-primary" />}
                        </div>
                        <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
                            Telemetria contínua a cada ~5 min; métricas dos fabricantes atualizadas em segundo plano (cache).
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <FonteChip label="Telemetria" status={ultimaTelemetria ? 'ok' : 'idle'} at={ultimaTelemetria} />
                            {FONTES_EXTERNAS.map(f => (
                                <FonteChip
                                    key={f.key}
                                    label={f.label}
                                    status={fabricantesRefreshing ? 'loading' : (fontesSync[f.key] ? 'ok' : 'error')}
                                    at={fontesSync[f.key]}
                                />
                            ))}
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setIsAlarmesOpen(true)}
                            className="relative flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all shadow-sm"
                        >
                            <Siren size={16} className={eventosRecentes.length ? 'text-rose-500' : 'text-gray-500'} />
                            Alarmes
                            {eventosRecentes.length > 0 ? (
                                <span
                                    className={`ml-0.5 px-1.5 py-0.5 rounded-full text-white text-[10px] font-bold tabular-nums ${eventosAbertos ? 'bg-rose-500' : 'bg-gray-400 dark:bg-gray-600'}`}
                                    title={eventosAbertos
                                        ? `${plural(eventosAbertos, 'evento ainda aberto', 'eventos ainda abertos')} nas últimas 72h`
                                        : 'Tudo já normalizou nas últimas 72h'}
                                >
                                    {eventosRecentes.length}
                                </span>
                            ) : null}
                        </button>
                        <button
                            onClick={handleManualSync}
                            disabled={isRefreshing}
                            className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-all shadow-sm disabled:opacity-60 disabled:cursor-wait"
                        >
                            <CloudDownload size={16} className={(isRefreshing || fabricantesRefreshing) ? 'animate-pulse text-primary' : 'text-gray-500'} />
                            Sincronizar agora
                        </button>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="relative bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden group hover:border-blue-200 dark:hover:border-blue-800 transition-all">
                        <div className="flex justify-between items-start relative z-10">
                            <div>
                                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Potência Instantânea</p>
                                <h3 className="text-3xl font-montserrat font-bold text-gray-900 dark:text-white">
                                    {formatPower(potenciaTotal.somaW).split(' ')[0]}
                                    <span className="text-xl text-gray-400 font-semibold ml-1.5">{formatPower(potenciaTotal.somaW).split(' ')[1]}</span>
                                </h3>
                            </div>
                            <div className="p-3.5 bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-2xl relative shadow-inner">
                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full animate-ping opacity-75"></span>
                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full border-2 border-white dark:border-gray-800"></span>
                                <Activity size={24} />
                            </div>
                        </div>
                        <div className="mt-5 text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {potenciaTotal.reportando} de {unidadesExibidas.length} usinas reportando potência
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm transition-all hover:border-indigo-200 dark:hover:border-indigo-800">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Produção 30 Dias</p>
                                <h3 className="text-3xl font-montserrat font-bold text-gray-900 dark:text-white">
                                    {formatKwh(resumoEnergia['30d']).split(' ')[0]}
                                    <span className="text-xl text-gray-400 font-semibold ml-1.5">{formatKwh(resumoEnergia['30d']).split(' ')[1]}</span>
                                </h3>
                            </div>
                            <div className="p-3.5 bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 rounded-2xl shadow-inner">
                                <Zap size={24} />
                            </div>
                        </div>
                        <div className="mt-5 text-xs text-gray-500 dark:text-gray-400 font-medium">Soma das usinas nos últimos 30 dias</div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm transition-all hover:border-emerald-200 dark:hover:border-emerald-800">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Usinas Operando</p>
                                <h3 className="text-3xl font-montserrat font-bold text-gray-900 dark:text-white">
                                    {usinasOperando} <span className="text-xl text-gray-400 font-semibold">/ {unidadesExibidas.length}</span>
                                </h3>
                            </div>
                            <div className="p-3.5 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 rounded-2xl shadow-inner">
                                <CheckCircle2 size={24} />
                            </div>
                        </div>
                        <div className="mt-5 flex flex-col gap-1.5">
                            {usinasOperando === unidadesExibidas.length && usinasParciais === 0 ? (
                                <span className="text-emerald-600 font-bold bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400 px-2 py-1.5 rounded-md text-[10px] uppercase tracking-wider w-fit">Disponibilidade Total</span>
                            ) : (
                                <span className="text-gray-500 dark:text-gray-400 text-[10px] font-bold uppercase tracking-wider">
                                    {usinasParciais > 0 ? `${usinasParciais} usina${usinasParciais > 1 ? 's' : ''} parcial${usinasParciais > 1 ? 'is' : ''}` : 'Usinas gerando energia agora'}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="bg-white dark:bg-gray-800 p-6 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm transition-all hover:border-violet-200 dark:hover:border-violet-800">
                        <div className="flex justify-between items-start">
                            <div>
                                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Capacidade Cadastrada</p>
                                <h3 className="text-3xl font-montserrat font-bold text-gray-900 dark:text-white">
                                    {capacidadeTotalMwp.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    <span className="text-xl text-gray-400 font-semibold ml-1.5">MWp</span>
                                </h3>
                            </div>
                            <div className="p-3.5 bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-400 rounded-2xl shadow-inner">
                                <Battery size={24} />
                            </div>
                        </div>
                        <div className="mt-5 text-xs text-gray-500 dark:text-gray-400 font-medium">
                            {unidadesExibidas.filter(u => u.capacidade_instalada).length} de {unidadesExibidas.length} usinas com capacidade cadastrada
                        </div>
                    </div>
                </div>

                {/* Produção */}
                <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 space-y-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <h2 className="font-poppins font-bold text-gray-900 dark:text-white text-lg">Produção de Energia</h2>
                        <span className="text-xs text-gray-500 dark:text-gray-400">Delta do acumulado da telemetria; plataforma do fabricante como fallback</span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {([
                            { label: 'Último 1 dia', key: '1d' },
                            { label: 'Últimos 15 dias', key: '15d' },
                            { label: 'Últimos 30 dias', key: '30d' },
                            { label: 'Últimos 12 meses', key: '12m' },
                            { label: 'Total até agora', key: 'total' },
                        ] as const).map(item => (
                            <div key={item.key} className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-900/30 px-4 py-3">
                                <div className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-bold">{item.label}</div>
                                <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white tabular-nums">{formatKwh(resumoEnergia[item.key])}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Listagem de Usinas */}
                <div className="bg-white dark:bg-gray-800 rounded-3xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
                    <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 space-y-3">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <h2 className="font-poppins font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                <RadioTower size={18} className="text-primary" />
                                Lista de Usinas Monitoradas
                            </h2>
                            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">{unidadesFiltradas.length} de {unidadesExibidas.length} usinas</span>
                        </div>
                        <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
                            <div className={`relative flex-1 min-w-0 transition-all duration-300 ${isSearchFocused ? 'lg:max-w-md' : 'lg:max-w-xs'}`}>
                                <div className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors duration-200 ${isSearchFocused ? 'text-primary' : 'text-gray-400 dark:text-gray-500'}`}>
                                    <Search size={15} />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Buscar usina ou ID..."
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    onFocus={() => setIsSearchFocused(true)}
                                    onBlur={() => setIsSearchFocused(false)}
                                    className={`w-full bg-white dark:bg-gray-800 border rounded-full pl-10 pr-9 py-2 text-sm font-medium text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 outline-none transition-all duration-200 shadow-sm ${isSearchFocused ? 'border-primary ring-2 ring-primary/20 shadow-md' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'}`}
                                />
                                {searchFilter && (
                                    <button
                                        onClick={() => setSearchFilter('')}
                                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-150"
                                    >
                                        <X size={13} />
                                    </button>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <FilterDropdown<'all' | 'operando' | 'falha'>
                                    tag="Filtrar por"
                                    icon={<Filter size={12} />}
                                    highlight={statusFilter !== 'all'}
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    valueLabel={statusFilter === 'all' ? 'Todos os Status' : statusFilter === 'operando' ? 'Operando' : 'Atenção/Falha'}
                                    options={[
                                        { value: 'all', label: 'Todos os Status', icon: <Filter size={13} /> },
                                        { value: 'operando', label: 'Apenas Operando', icon: <CheckCircle2 size={13} /> },
                                        { value: 'falha', label: 'Atenção / Falha', icon: <AlertTriangle size={13} /> },
                                    ]}
                                />
                                <FilterDropdown<'nome' | 'potencia' | 'prod30d'>
                                    tag="Ordenar por"
                                    icon={<ArrowUpDown size={12} />}
                                    highlight={sortFilter !== 'nome'}
                                    value={sortFilter}
                                    onChange={setSortFilter}
                                    valueLabel={sortFilter === 'nome' ? 'Nome' : sortFilter === 'potencia' ? 'Potência' : 'Prod 30D'}
                                    options={[
                                        { value: 'nome', label: 'Nome da Usina', icon: <RadioTower size={13} /> },
                                        { value: 'potencia', label: 'Potência Atual', icon: <Zap size={13} /> },
                                        { value: 'prod30d', label: 'Produção 30 Dias', icon: <Activity size={13} /> },
                                    ]}
                                />
                                <FilterDropdown<6 | 12 | 24>
                                    tag="Janela de análise"
                                    icon={<Clock size={12} />}
                                    highlight={janelaAnaliseHoras !== 24}
                                    value={janelaAnaliseHoras}
                                    onChange={setJanelaAnaliseHoras}
                                    valueLabel={`${janelaAnaliseHoras}h`}
                                    options={[
                                        { value: 6, label: '6 Horas', icon: <Clock size={13} /> },
                                        { value: 12, label: '12 Horas', icon: <Clock size={13} /> },
                                        { value: 24, label: '24 Horas', icon: <Clock size={13} /> },
                                    ]}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm whitespace-nowrap">
                            <thead className="bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                                <tr>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider w-[200px]">Usina</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider w-[130px]">UC / ID</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider w-[140px]">Status</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Potência Agora</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Prod 1D</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Prod 15D</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Prod 30D</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Prod 12M</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Prod Total</th>
                                    <th className="px-6 py-5 font-bold text-gray-400 uppercase text-[10px] tracking-wider text-right">Dado da Usina</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                                {unidadesFiltradas.length === 0 && (
                                    <tr>
                                        <td colSpan={10} className="px-6 py-16 text-center text-gray-500 font-medium">
                                            Nenhuma usina corresponde aos filtros aplicados.
                                        </td>
                                    </tr>
                                )}
                                {unidadesFiltradas.map(usina => {
                                    const site = usina.site_id_api;
                                    const m = metricasPorUsina[site];
                                    const st = statusReal[site];
                                    const potencia = getPotenciaAtualW(site);
                                    const dadoAt = getDadoUsinaAt(site);
                                    const freshness = getFreshness(dadoAt);
                                    const display = getDisplay(usina);
                                    const isSelected = isDetailModalOpen && site === selectedSiteId;
                                    const isParcial = st?.state === 'parcial';

                                    const num = (v: number | null | undefined, cls = 'text-gray-900 dark:text-white') =>
                                        v != null ? <span className={cls}>{formatKwh(v)}</span> : <span className="text-gray-400 dark:text-gray-500">N/D</span>;

                                    return (
                                        <tr
                                            key={site}
                                            onClick={() => {
                                                setSelectedSiteId(site);
                                                setIsDetailModalOpen(true);
                                                // Filtro de outra usina não pode viajar junto: a tabela
                                                // apareceria vazia sem explicação.
                                                setFiltroLeitura('todas');
                                                setBuscaLeitura('');
                                                setAlarmeExpandido(null);
                                            }}
                                            className={`transition-all duration-200 group cursor-pointer border-l-4 ${isSelected
                                                ? 'bg-emerald-50/70 dark:bg-emerald-900/20 border-emerald-500'
                                                : isParcial
                                                    ? 'bg-amber-50/30 dark:bg-amber-900/10 border-amber-400/50 hover:bg-amber-50/50'
                                                    : 'hover:bg-gray-50/80 dark:hover:bg-gray-750 border-transparent'}`}
                                        >
                                            <td className="px-6 py-4">
                                                <div className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                                    {display.marca}
                                                    {isSelected && (
                                                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 rounded-full">Selecionada</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="text-sm text-gray-700 dark:text-gray-300 font-mono font-semibold">{display.uc}</span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusBadge state={st?.state || 'desconhecido'} statusCode={st?.code ?? null} lastUpdateTime={dadoAt} />
                                            </td>
                                            <td className="px-6 py-4 text-right font-bold tabular-nums">
                                                {potencia != null
                                                    ? <span className="text-blue-600 dark:text-blue-400">{formatPower(potencia)}</span>
                                                    : <span className="text-gray-400 dark:text-gray-500 font-medium">N/D</span>}
                                            </td>
                                            <td className="px-6 py-4 text-right font-semibold tabular-nums">{num(m?.prod1d)}</td>
                                            <td className="px-6 py-4 text-right font-semibold tabular-nums">{num(m?.prod15d)}</td>
                                            <td className="px-6 py-4 text-right font-semibold tabular-nums">{num(m?.prod30d)}</td>
                                            <td className="px-6 py-4 text-right font-semibold tabular-nums">{num(m?.prod12m)}</td>
                                            <td className="px-6 py-4 text-right font-bold tabular-nums">{num(m?.prodTotal, 'text-emerald-600 dark:text-emerald-400')}</td>
                                            <td className="px-6 py-4 text-right">
                                                <div
                                                    className={`text-xs font-medium inline-flex px-2 py-1 rounded-md ${freshness === 'fresh'
                                                        ? 'text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                                                        : freshness === 'stale'
                                                            ? 'text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20'
                                                            : freshness === 'old'
                                                                ? 'text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20'
                                                                : 'text-gray-500 bg-gray-50 dark:bg-gray-800/50'}`}
                                                    title={dadoAt ? formatDateTime(dadoAt) : 'Sem dado'}
                                                >
                                                    {dadoAt ? getRelativeTime(dadoAt) : 'Sem dado'}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {isAlarmesOpen && createPortal(
                <div className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setIsAlarmesOpen(false)} />
                    {/* flex-col + flex-1/min-h-0 no corpo: o cabeçalho ocupa o que
                        precisar e a lista fica com o resto. Antes o corpo tinha
                        max-h-[calc(92vh-140px)], com 140px chutados para a altura do
                        cabeçalho — qualquer linha a mais no título e a lista passava
                        do cartão. min-h-0 é obrigatório: item de flex nasce com
                        min-height:auto e sem isso se recusa a encolher para rolar. */}
                    <div
                        className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-3xl max-h-[calc(100vh-2rem)] flex flex-col shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-in zoom-in-95 slide-in-from-top-4 duration-300"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-rose-50 via-amber-50 to-white dark:from-rose-900/20 dark:via-amber-900/20 dark:to-gray-800">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-[11px] uppercase tracking-[0.18em] text-rose-700 dark:text-rose-300 font-bold">Histórico de alarmes</div>
                                    <h3 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white mt-1">Alarmes das geradoras</h3>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-xl">
                                        Cada cartão é um evento: a mesma falha na mesma usina, mesmo quando atinge vários
                                        inversores. Clique para ver o que significa, o efeito na geração e o que fazer.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsAlarmesOpen(false)}
                                    className="p-2 rounded-lg hover:bg-white/70 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition-colors"
                                    aria-label="Fechar"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-3">
                            {eventosAlarme.length === 0 ? (
                                <div className="text-center py-14">
                                    <CheckCircle2 size={40} className="mx-auto text-emerald-500" />
                                    <p className="mt-3 font-semibold text-gray-700 dark:text-gray-200">Nenhum alarme registrado</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto">
                                        Os inversores avisam aqui assim que detectam uma falha. As quatro marcas estão
                                        ligadas: a Chint empurra por webhook, e Canadian, Growatt e SAJ são lidas de
                                        hora em hora.
                                    </p>
                                </div>
                            ) : null}

                            {eventosAlarme.map(ev => (
                                <CartaoEvento
                                    key={ev.id}
                                    evento={ev}
                                    nomeUsina={nomePorSite[ev.representante.site_id_api]
                                        || ev.representante.site_name
                                        || ev.representante.site_id_api}
                                    aberto={alarmeExpandido === ev.id}
                                    onToggle={() => setAlarmeExpandido(alarmeExpandido === ev.id ? null : ev.id)}
                                />
                            ))}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {usinaSelecionada && isDetailModalOpen && createPortal(
                <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setIsDetailModalOpen(false)} />
                    {/* O limite de altura fica no CONTAINER QUE ROLA, não no cartão.
                        Antes o cartão tinha max-h-[92vh] + overflow-hidden e o filho
                        usava h-full: como o pai só tinha altura MÁXIMA, e não altura
                        definida, o h-full não resolvia, o filho crescia junto com o
                        conteúdo e o overflow-hidden cortava o resto sem dar barra —
                        o painel simplesmente não rolava. */}
                    <div
                        className="relative bg-white dark:bg-gray-800 rounded-2xl w-full max-w-6xl shadow-2xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-in zoom-in-95 slide-in-from-top-4 duration-300"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="max-h-[calc(100vh-2rem)] overflow-y-auto overscroll-contain">
                            <div className="sticky top-0 z-20 px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-emerald-50 via-cyan-50 to-blue-50 dark:from-emerald-900/20 dark:via-cyan-900/20 dark:to-blue-900/20 backdrop-blur">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300 font-bold">Painel Executivo da UVC</div>
                                        <h3 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white mt-1">{usinaSelecionada.nome_usina}</h3>
                                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                                            <span className="px-2.5 py-1 rounded-full bg-white/80 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 font-mono text-gray-600 dark:text-gray-300">
                                                ID API: {usinaSelecionada.site_id_api}
                                            </span>
                                            <StatusBadge
                                                state={statusReal[usinaSelecionada.site_id_api]?.state || 'desconhecido'}
                                                statusCode={statusReal[usinaSelecionada.site_id_api]?.code ?? null}
                                                lastUpdateTime={getDadoUsinaAt(usinaSelecionada.site_id_api)}
                                            />
                                            <span className="px-2.5 py-1 rounded-full bg-white/80 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">
                                                Último dado: {getRelativeTime(getDadoUsinaAt(usinaSelecionada.site_id_api))}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setIsDetailModalOpen(false)}
                                        className="inline-flex items-center justify-center rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 w-10 h-10 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                        aria-label="Fechar painel"
                                    >
                                        <X size={18} />
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Coletor vivo + usina muda é o estado que mais engana: a tela
                                    parece atualizada porque o robô grava de 5 em 5 min, mas o
                                    inversor não manda nada há dias. Dizer isso em voz alta. */}
                                {diagnosticoUsina ? (
                                    <div className={`rounded-2xl border p-4 flex items-start gap-3 ${diagnosticoUsina.grave
                                        ? 'border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-900/20'
                                        : 'border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20'}`}>
                                        <AlertTriangle size={18} className={`mt-0.5 flex-shrink-0 ${diagnosticoUsina.grave ? 'text-rose-600 dark:text-rose-400' : 'text-amber-600 dark:text-amber-400'}`} />
                                        <div className="text-sm">
                                            <p className={`font-semibold ${diagnosticoUsina.grave ? 'text-rose-800 dark:text-rose-200' : 'text-amber-800 dark:text-amber-200'}`}>
                                                {diagnosticoUsina.titulo}
                                            </p>
                                            <p className={diagnosticoUsina.grave ? 'text-rose-700/90 dark:text-rose-300/90' : 'text-amber-700/90 dark:text-amber-300/90'}>
                                                {diagnosticoUsina.detalhe}
                                            </p>
                                        </div>
                                    </div>
                                ) : null}

                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                                    <div className="rounded-2xl border border-blue-100 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-900/20 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs uppercase tracking-wider text-blue-700 dark:text-blue-300 font-bold">Potência Atual</span>
                                            <Gauge size={16} className="text-blue-600 dark:text-blue-300" />
                                        </div>
                                        <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white tabular-nums">{formatPower(potenciaAtualSelecionada)}</div>
                                        <div className="mt-2 text-xs text-blue-700/90 dark:text-blue-200">Fator de capacidade: {fatorCapacidadeAtual !== null ? `${fatorCapacidadeAtual}%` : 'N/A'}</div>
                                    </div>

                                    <div className="rounded-2xl border border-cyan-100 dark:border-cyan-900/40 bg-cyan-50/60 dark:bg-cyan-900/20 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs uppercase tracking-wider text-cyan-700 dark:text-cyan-300 font-bold">Pico e Média ({janelaAnaliseHoras}h)</span>
                                            <LineChart size={16} className="text-cyan-600 dark:text-cyan-300" />
                                        </div>
                                        <div className="mt-2 text-base font-bold text-gray-900 dark:text-white tabular-nums">Pico: {formatPower(potenciaPicoW)}</div>
                                        <div className="text-sm text-cyan-800 dark:text-cyan-200 tabular-nums">Média: {formatPower(potenciaMediaW)}</div>
                                        <div className="mt-2 text-xs text-cyan-700/90 dark:text-cyan-200">Fator médio: {fatorCapacidadeMedio !== null ? `${fatorCapacidadeMedio}%` : 'N/A'}</div>
                                    </div>

                                    <div className="rounded-2xl border border-emerald-100 dark:border-emerald-900/40 bg-emerald-50/60 dark:bg-emerald-900/20 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-bold">Energia</span>
                                            <Zap size={16} className="text-emerald-600 dark:text-emerald-300" />
                                        </div>
                                        <div className="mt-2 text-sm text-gray-900 dark:text-white font-semibold">1 dia: <span className="tabular-nums">{formatKwh(metricasPorUsina[usinaSelecionada.site_id_api]?.prod1d ?? null)}</span></div>
                                        <div className="text-sm text-gray-900 dark:text-white font-semibold">30 dias: <span className="tabular-nums">{formatKwh(metricasPorUsina[usinaSelecionada.site_id_api]?.prod30d ?? null)}</span></div>
                                        <div className="text-sm text-gray-900 dark:text-white font-semibold">Total: <span className="tabular-nums">{formatKwh(metricasPorUsina[usinaSelecionada.site_id_api]?.prodTotal ?? null)}</span></div>
                                    </div>

                                    <div className="rounded-2xl border border-amber-100 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/20 p-4">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs uppercase tracking-wider text-amber-700 dark:text-amber-300 font-bold">Janela {janelaAnaliseHoras}h</span>
                                            <Database size={16} className="text-amber-600 dark:text-amber-300" />
                                        </div>
                                        <div className="mt-2 text-sm text-gray-900 dark:text-white font-semibold tabular-nums">Energia na janela: {formatKwh(energiaJanelaKwh)}</div>
                                        <div className="text-sm text-gray-900 dark:text-white font-semibold tabular-nums">{pontosCalculo.length} amostras</div>
                                        <div className="mt-2 text-xs text-amber-700/90 dark:text-amber-200">Capacidade: {usinaSelecionada.capacidade_instalada != null ? `${usinaSelecionada.capacidade_instalada.toLocaleString('pt-BR')} MWp` : 'não cadastrada'}</div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                                    <div className="xl:col-span-2 rounded-2xl border border-gray-100 dark:border-gray-700 p-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                                            <div>
                                                <h4 className="font-semibold text-gray-900 dark:text-white">Curva de Potência</h4>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {serie.length} pontos · {PERIODOS.find(p => p.chave === periodo)!.label.toLowerCase()}
                                                </span>
                                            </div>
                                            {/* Presets antes de qualquer intervalo customizado: é o filtro
                                                que todo mundo procura primeiro. */}
                                            <div className="flex flex-wrap gap-1 rounded-lg bg-gray-100 dark:bg-gray-700/50 p-1" role="group" aria-label="Período">
                                                {PERIODOS.map(p => (
                                                    <button
                                                        key={p.chave}
                                                        onClick={() => setPeriodo(p.chave)}
                                                        aria-pressed={periodo === p.chave}
                                                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${periodo === p.chave
                                                            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                                                    >{p.label}</button>
                                                ))}
                                            </div>
                                        </div>
                                        <CurvaPotencia
                                            pontos={serie}
                                            granularidade={PERIODOS.find(p => p.chave === periodo)!.gran}
                                            carregando={carregandoSerie}
                                        />

                                        {/* Números da janela escolhida. Ficam sob o gráfico, e não
                                            nos cards do topo, porque aqueles são sempre 24h — misturar
                                            os dois faria "pico" significar duas coisas na mesma tela. */}
                                        {statsPeriodo ? (
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-bold">Energia no período</div>
                                                    <div className="text-lg font-bold tabular-nums text-emerald-600 dark:text-emerald-400">{formatKwh(statsPeriodo.energia)}</div>
                                                </div>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-bold">Pico do período</div>
                                                    <div className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{formatPower(statsPeriodo.picoW)}</div>
                                                    {statsPeriodo.picoEm ? (
                                                        <div className="text-[11px] text-gray-400 dark:text-gray-500">{formatDateTime(statsPeriodo.picoEm)}</div>
                                                    ) : null}
                                                </div>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-bold">Média ponderada</div>
                                                    <div className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">{formatPower(Math.round(statsPeriodo.mediaW))}</div>
                                                    <div className="text-[11px] text-gray-400 dark:text-gray-500">
                                                        {statsPeriodo.fatorPeriodo != null ? `fator ${statsPeriodo.fatorPeriodo.toFixed(1)}%` : 'sem capacidade cadastrada'}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-bold">Cobertura</div>
                                                    <div className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                                                        {statsPeriodo.comGeracao}/{serie.length}
                                                    </div>
                                                    <div className="text-[11px] text-gray-400 dark:text-gray-500">
                                                        {statsPeriodo.amostras.toLocaleString('pt-BR')} amostras
                                                    </div>
                                                </div>
                                            </div>
                                        ) : null}

                                        {statsPeriodo?.melhorBalde && statsPeriodo.melhorBalde.geracao_kwh > 0 ? (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                                                Melhor {PERIODOS.find(p => p.chave === periodo)!.gran === 'hora' ? 'hora' : PERIODOS.find(p => p.chave === periodo)!.gran === 'dia' ? 'dia' : 'mês'} da janela:
                                                {' '}<span className="font-semibold text-gray-700 dark:text-gray-200">{formatDateTime(statsPeriodo.melhorBalde.bucket)}</span>
                                                {' '}com <span className="font-semibold text-gray-700 dark:text-gray-200">{formatKwh(statsPeriodo.melhorBalde.geracao_kwh)}</span>.
                                            </p>
                                        ) : null}
                                    </div>

                                    <div className="rounded-2xl border border-gray-100 dark:border-gray-700 p-4 space-y-3">
                                        <h4 className="font-semibold text-gray-900 dark:text-white">Metadados</h4>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-2">
                                            <div className="flex items-start gap-2">
                                                <Clock3 size={14} className="mt-0.5 text-gray-400" />
                                                <div>
                                                    <div className="font-semibold text-gray-700 dark:text-gray-200">Última telemetria (coletor)</div>
                                                    <div>{leituraSelecionada ? formatDateTime(leituraSelecionada.data_leitura) : 'Sem telemetria contínua'}</div>
                                                </div>
                                            </div>
                                            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2.5 border border-gray-100 dark:border-gray-700">
                                                <div className="text-[10px] uppercase tracking-wider text-gray-400">Último dado na plataforma do fabricante</div>
                                                <div className="font-semibold text-gray-700 dark:text-gray-200">{formatDateTime(metrics[usinaSelecionada.site_id_api]?.last_update_time || null)}</div>
                                            </div>
                                            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2.5 border border-gray-100 dark:border-gray-700">
                                                <div className="text-[10px] uppercase tracking-wider text-gray-400">Cadastro da usina atualizado em</div>
                                                <div className="font-semibold text-gray-700 dark:text-gray-200">{formatDateTime(usinaSelecionada.atualizado_em)}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Alarmes da própria usina. Antes era preciso fechar este painel
                                    e abrir o histórico geral para descobrir se ela tinha alarme —
                                    justamente a informação que explica uma queda de geração. */}
                                <div className="rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
                                        <h4 className="font-semibold text-gray-900 dark:text-white">Alarmes desta usina</h4>
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {eventosDaUsina.length === 0
                                                ? 'nenhum registrado'
                                                : `${plural(eventosDaUsina.length, 'evento', 'eventos')} · ${plural(eventosDaUsina.filter(e => !e.encerrado).length, 'sem fim registrado', 'sem fim registrado')}`}
                                        </span>
                                    </div>
                                    {eventosDaUsina.length === 0 ? (
                                        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                            Nenhum alarme registrado para esta usina.
                                        </div>
                                    ) : (
                                        <div className="max-h-96 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700/60">
                                            {eventosDaUsina.map(ev => {
                                                const cat = ev.representante.catalogo;
                                                const st = SEVERIDADE_STYLE[cat?.severidade || 'atencao'];
                                                const aberto = alarmeExpandido === ev.id;
                                                return (
                                                    <div key={ev.id}>
                                                        <button
                                                            onClick={() => setAlarmeExpandido(aberto ? null : ev.id)}
                                                            aria-expanded={aberto}
                                                            className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
                                                        >
                                                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${ev.encerrado ? 'bg-gray-300 dark:bg-gray-600' : st.dot}`} />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                    {cat?.titulo || ev.representante.error_code_info || ev.representante.error_code}
                                                                </div>
                                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                                    {formatDateTime(ev.inicio)}
                                                                    {' · '}{plural(ev.inversores.length, 'inversor', 'inversores')}
                                                                    {ev.encerrado
                                                                        ? ` · normalizado${duracaoEvento(ev) ? ` em ${duracaoEvento(ev)}` : ''}`
                                                                        : ' · sem fim registrado'}
                                                                </div>
                                                            </div>
                                                            <ChevronRight size={16} className={`flex-shrink-0 mt-1 text-gray-400 transition-transform ${aberto ? 'rotate-90' : ''}`} />
                                                        </button>
                                                        {aberto ? (
                                                            <div className="px-4 pb-4 pt-1 bg-gray-50/60 dark:bg-gray-900/30">
                                                                <DetalheAlarme evento={ev} />
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>

                                <div className="rounded-2xl border border-gray-100 dark:border-gray-700 overflow-hidden">
                                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-100 dark:border-gray-700 space-y-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                            <h4 className="font-semibold text-gray-900 dark:text-white">Leituras capturadas</h4>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                                                {leiturasFiltradas.length === leiturasModal.length
                                                    ? `${leiturasModal.length}${leiturasModal.length >= 500 ? ' mais recentes' : ''} no período`
                                                    : `${leiturasFiltradas.length} de ${leiturasModal.length}`}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <div className="flex rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-0.5" role="group" aria-label="Filtrar leituras">
                                                {([
                                                    ['todas', 'Todas'],
                                                    ['geracao', 'Com geração'],
                                                    ['zeradas', 'Zeradas'],
                                                ] as const).map(([chave, rotulo]) => (
                                                    <button
                                                        key={chave}
                                                        onClick={() => setFiltroLeitura(chave)}
                                                        aria-pressed={filtroLeitura === chave}
                                                        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${filtroLeitura === chave
                                                            ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                                                            : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'}`}
                                                    >{rotulo}</button>
                                                ))}
                                            </div>
                                            <div className="relative flex-1 min-w-[150px]">
                                                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                                <input
                                                    value={buscaLeitura}
                                                    onChange={e => setBuscaLeitura(e.target.value)}
                                                    placeholder="Filtrar por data ou hora (ex.: 28/08, 14:)"
                                                    aria-label="Filtrar leituras por data ou hora"
                                                    className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                />
                                            </div>
                                            <button
                                                onClick={exportarLeiturasCsv}
                                                disabled={leiturasFiltradas.length === 0}
                                                className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                                            >
                                                <CloudDownload size={13} /> CSV
                                            </button>
                                        </div>
                                    </div>
                                    {/* Rola dentro do próprio cartão: 500 linhas soltas empurrariam
                                        o resto do painel para fora da tela. */}
                                    <div className="overflow-auto max-h-80">
                                        <table className="w-full text-sm whitespace-nowrap">
                                            <thead className="bg-white dark:bg-gray-800 sticky top-0 z-10">
                                                <tr>
                                                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-wider text-gray-400">Timestamp</th>
                                                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-gray-400">Potência</th>
                                                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-gray-400">Hoje</th>
                                                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-gray-400">Mês</th>
                                                    <th className="text-right px-4 py-3 text-[10px] uppercase tracking-wider text-gray-400">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                                                {leiturasFiltradas.map(item => (
                                                    <tr key={`${item.id}-${item.data_leitura}`} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                                                        <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{formatDateTime(item.data_leitura)}</td>
                                                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-gray-900 dark:text-white">{formatPower(item.potencia_atual_w)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatKwh(item.energia_hoje_kwh)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatKwh(item.energia_mes_kwh)}</td>
                                                        <td className="px-4 py-3 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatKwh(item.energia_total_kwh)}</td>
                                                    </tr>
                                                ))}
                                                {leiturasFiltradas.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={5} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                                            {carregandoSerie
                                                                ? 'Carregando leituras…'
                                                                : leiturasModal.length > 0
                                                                    ? 'Nenhuma leitura bate com o filtro.'
                                                                    : 'Nenhuma leitura de telemetria nesta janela — os números vêm direto da plataforma do fabricante.'}
                                                        </td>
                                                    </tr>
                                                ) : null}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default PainelUsinas;
